import {
  isStaleAnswersRevisionError,
  type Document,
  type DocumentAnswers,
  type TranscriptorApiClient,
} from '@/shared/api/transcriptor';

import type { DocumentTemplate } from '@/entities/templates';

import { isLiveProtocolDocument } from '@/features/audioRecording';

import { draftAnswerToChange } from './documentAnswers';
import {
  followFieldsByText,
  moveAnswerKeys,
  type TemplateDraft,
} from './templateDraft';

export type PresetAnswersResult = {
  saved: { documentId: string; answers: DocumentAnswers | null }[];
  // Working copies whose values did not all reach their document.
  failed: number;
};

const hasPresetAnswers = (draft: TemplateDraft) =>
  Object.keys(draft.answers).length > 0;

export const hasPresetAnswersIn = (drafts: TemplateDraft[]) =>
  drafts.some(hasPresetAnswers);

// Values chosen before the start become manual answers of the protocol
// documents the new encounter created. Answer keys point at the version each
// document pinned, which is loaded when it is not the working copy's own one;
// the values then follow their fields into it by text.
export const pushPresetAnswers = async ({
  api,
  encounterId,
  documents,
  drafts,
  loadTemplate,
}: {
  api: Pick<
    TranscriptorApiClient,
    'getDocumentAnswers' | 'updateDocumentAnswers'
  >;
  encounterId: string;
  documents: Document[];
  drafts: TemplateDraft[];
  loadTemplate: (templateId: string) => Promise<DocumentTemplate>;
}): Promise<PresetAnswersResult> => {
  const results = await Promise.allSettled(
    drafts.filter(hasPresetAnswers).map(async (draft) => {
      const document = documents.find(
        (item) =>
          isLiveProtocolDocument(item) &&
          item.template_series_id === draft.template.series_id,
      );
      if (!document?.id || !document.template_id) {
        throw new Error(
          `No protocol document for template series ${draft.template.series_id}`,
        );
      }
      const pinned = document.template_id === draft.template.id;
      const template = pinned
        ? draft.template
        : await loadTemplate(document.template_id);
      const answers = Object.entries(
        pinned
          ? draft.answers
          : moveAnswerKeys(
              draft.answers,
              followFieldsByText(draft.template, template),
            ),
      );
      const changes = answers.flatMap(([key, value]) => {
        const change = draftAnswerToChange({ template, key, value });
        return change ? [change] : [];
      });
      if (!changes.length) {
        throw new Error(
          `No preset answer matches the pinned template ${document.template_id}`,
        );
      }
      const documentId = document.id;
      const submit = (baseRevision: number) =>
        api.updateDocumentAnswers({
          encounterId,
          documentId,
          baseRevision,
          changes,
        });
      // A live fill may store answers first; the values are then applied once
      // on top of the stored revision.
      const snapshot = await submit(0).catch(async (error: unknown) => {
        if (!isStaleAnswersRevisionError(error)) {
          throw error;
        }
        const current = await api.getDocumentAnswers({
          encounterId,
          documentId,
        });
        return submit(current?.revision ?? 0);
      });
      return {
        documentId,
        answers: snapshot,
        complete: changes.length === Object.keys(draft.answers).length,
      };
    }),
  );
  return {
    saved: results.flatMap((result) =>
      result.status === 'fulfilled'
        ? [
            {
              documentId: result.value.documentId,
              answers: result.value.answers,
            },
          ]
        : [],
    ),
    failed: results.filter(
      (result) => result.status === 'rejected' || !result.value.complete,
    ).length,
  };
};
